import moment from 'moment';
import 'moment-timezone';
import AnnouncementMonitor from './statusBar/announcementMonitor';
import { mapAnnouncement } from './statusBar/announcementsService';
import { getStatusBarClass, statusComponent } from './statusBar/components';
import paginateMessage from './statusBar/messagePaginator';
import StatusManager from './statusBar/statusManager';

const Statusbar = {
	animationSpeed: 800,
	// eslint-disable-next-line no-magic-numbers
	maxStatusAge: moment.duration(12, 'hours'),  // how long to include a status in the cycle
	maxStatusNumber: 10,  // how many statuses to keep in rotation
	closeCurrentAnnouncement: null,
	_timeFormatter: null,

	init: function() {
		const firstStart = !this.statusManager;

		if (!this.announcementMonitor) {
			this.announcementMonitor = new AnnouncementMonitor(
				this.getActiveAnnouncements.bind(this),
				this.cancelAnnouncement.bind(this),
				this.addAnnouncement.bind(this)
			);
		}

		if (!this.statusManager) {
			this.statusManager = new StatusManager(
				Screen.options.initialStatuses,
				{
					showStatus: this.showStatus.bind(this),
					showAnnouncement: this.showAnnouncement.bind(this),
					updateActiveMoment: this.updateActiveMoment.bind(this),
				},
				this.announcementMonitor.unobserve.bind(this.announcementMonitor));
		}

		// start cycle with latest item
		Screen.elements.statusbar.show();
		this.statusManager.start();
		this.announcementMonitor.start();
		this.updateTime();

		if (firstStart) {
			const that = this;
			this.getActiveAnnouncements((array) => {
				for (const announcement of array) {
					that.addAnnouncement(announcement);
				}
			});
		}
	},

	destroy: function() {
		// clear any timeouts, unbind events and reset variables
		this.statusManager.stop();
		this.announcementMonitor.stop();
		Screen.elements.statusbar.hide();
	},

	updateTime: function() {
		if (!this._timeFormatter) {
			const use12h = ['en_US', 'en_GB'].indexOf(Screen.options.locale) >= 0;
			// Use explicit BCP-47 locales so the output format is deterministic
			// regardless of the TV's system locale (e.g. da-DK would give "p.m." not "PM").
			// en-US: "2:05:09 PM"  |  en-GB: "14:05:09"
			const locale = use12h ? 'en-US' : 'en-GB';
			this._timeFormatter = new Intl.DateTimeFormat(locale, {
				timeZone: Screen.options.timezone,
				hour: use12h ? 'numeric' : '2-digit',
				minute: '2-digit',
				second: '2-digit',
				hour12: use12h,
			});
		}
		Screen.elements.statusbarTime.text(this._timeFormatter.format(new Date()));
	},

	addStatus: function(status) {
		this.statusManager.addStatus(status);
	},

	getActiveAnnouncements: function(callback) {
		log('getting active announcements');
		$.ajax({
			type: 'GET',
			contentType: 'application/json; charset=utf-8',
			url: `/api/v2/screens/${Screen.options.screenId}/shown_announcements/`,
			beforeSend: function(request) {
				if (Screen.options.publicUrlToken) {
					request.setRequestHeader('publicUrlToken', Screen.options.publicUrlToken);
				}
			},
			success: function(array) {
				log(`Received active announcements (count: ${array.length})`);
				callback(array.map(a => mapAnnouncement(a)));
			},
			error: function(e) {
				// eslint-disable-next-line max-len
				log(`Error while getting announcements. HTTP status: ${e.statusText} (${e.status}). Error: ${e.responseText}`);
				// eslint-disable-next-line max-len
				Screen.logMessageToSentry(`Error while getting announcements. HTTP status: ${e.statusText} (${e.status})`, {
					responseText: e.responseText,
					responseJson: e.responseJSON,
				});
			},
		});
	},

	getCurrentlyShownAnnouncement: function(id) {
		return this.announcementMonitor.getAnnouncement(id);
	},

	addAnnouncement: function(announcement) {
		const titlePages = this.paginateAnnouncementText(announcement.title, announcement.author);
		if (titlePages.length > 1) {
			for (let i = titlePages.length - 1; i >= 0; --i) {
				const pageText = titlePages[i];
				// eslint-disable-next-line max-len
				const message = `${i > 0 ? '...' : ''}${pageText.trim()}${i < titlePages.length - 1 ? '...' : ''} (${i + 1}/${titlePages.length})`;
				this.pauseAnnouncements = i > 0;
				this.statusManager.addAnnouncement({ ...announcement, title: message });
			}
		} else {
			this.pauseAnnouncements = false;
			this.statusManager.addAnnouncement(announcement);
		}
		
		this.announcementMonitor.observe(announcement);
	},

	cancelAnnouncement: function(announcementId) {
		this.statusManager.removeAnnouncement(announcementId);
		this.announcementMonitor.unobserve(announcementId);
	},

	updateActiveMoment: function(status) {
		if (status && status.moment) {
			Screen.elements.statusbar.find('.statusbar-date').text(status.moment.fromNow());
		}
	},

	showAnnouncement: function(announcement) {
		if (!this.pauseAnnouncements) {
			this.activate(announcement);		
			Screen.elements.statusbar.attr('class', getStatusBarClass(announcement.type));
		}
	},

	showStatus: function(status) {
		this.activate(status);
		Screen.elements.statusbar.attr('class', '');
	},

	activate: function(status) {
		let elementsIn = $(null);
		const contents = Screen.elements.statusbar.find('.statusbar-content');
		const noRecentStatuses = Screen.elements.statusbar.find('.statusbar-content-item-empty');

		// animate out — CSS transition so the browser compositor handles it
		const elementsOut = contents.children().not(noRecentStatuses);
		const hadElementsOut = !!elementsOut.length;
		if (hadElementsOut) {
			elementsOut.css('transition', 'opacity ' + this.animationSpeed + 'ms ease-in-out');
			// Force reflow so the transition registers before opacity changes
			// eslint-disable-next-line no-unused-expressions
			elementsOut[0] && elementsOut[0].getBoundingClientRect();
			elementsOut.css('opacity', '0');
			const animSpeed = this.animationSpeed;
			elementsOut.each(function() {
				const el = $(this);
				// eslint-disable-next-line no-magic-numbers
				const t = setTimeout(function() { el.remove(); }, animSpeed + 100);
				el.one('transitionend', function() { clearTimeout(t); el.remove(); });
			});
		}

		// find/create element to animate in
		const marginTop = parseFloat(contents.height());
		if (status) {

			elementsIn =  $(statusComponent(status));
			noRecentStatuses.hide();

		} else {
			if (!noRecentStatuses.is(':visible')) {
				elementsIn = noRecentStatuses;
				elementsIn.detach();
				elementsIn.show();
			}
		}

		// animate in — CSS transition instead of jQuery.animate so the browser
		// handles the easing without a JS polling loop on the main thread
		elementsIn.css('marginTop', 0 - marginTop);
		contents.prepend(elementsIn);
		if (elementsIn) {
			if (hadElementsOut) {
				elementsIn.css('transition', 'margin-top ' + this.animationSpeed + 'ms ease-in-out');
				// Force reflow so the browser registers the starting marginTop before
				// we change it, otherwise the transition won't fire
				// eslint-disable-next-line no-unused-expressions
				elementsIn[0] && elementsIn[0].getBoundingClientRect();
				elementsIn.css('marginTop', 0);
			} else {
				elementsIn.css('marginTop', 0);
			}
		}
	},

	paginateAnnouncementText: function(text, author) {
		const testElement = Screen.elements.statusbar.find('.statusbar-width-test');
		const clockElement = Screen.elements.statusbar.find('.statusbar-label');

		const authorTestElement = Screen.elements.statusbar.find('.statusbar-width-author-test');
		const dateElement = Screen.elements.statusbar.find('.statusbar-date');
		authorTestElement.text(`${Screen.trans('by')} ${author}`);

		const maxAvailableWidth = Screen.elements.statusbar.outerWidth()
		- clockElement.outerWidth()
		// eslint-disable-next-line no-magic-numbers
		- (dateElement.outerWidth() || 150)
		// eslint-disable-next-line no-magic-numbers
		- /*padding*/20 
		- authorTestElement.outerWidth()
		// eslint-disable-next-line no-magic-numbers
		- 300/*padding, margins, etc*/;

		const parts = paginateMessage(text, testElement, maxAvailableWidth);

		return parts;

	},
};

export default Statusbar;
